import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { dashboard, catalogAdmin } from '../api/index.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Upload, CalendarDays, AlertTriangle, PackageCheck, Loader2, Database, RefreshCw, Users } from 'lucide-react';

const SENTINEL = 'ig.field.type.delete';

function safeDate(v: unknown): string {
  if (!v || v === SENTINEL) return '—';
  try {
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? '—' : format(d, 'MMM d, yyyy');
  } catch { return '—'; }
}

function safeStatus(v: unknown): string {
  if (!v || v === SENTINEL) return '—';
  return String(v);
}

const AT_RISK_LABELS: Record<string, { label: string; color: string }> = {
  release_imminent_draft: { label: 'Release imminent — still draft', color: 'text-red-700 bg-red-50' },
  releasing_soon_draft: { label: 'Releasing soon — not published', color: 'text-yellow-700 bg-yellow-50' },
  missing_publisher: { label: 'Missing internal publisher', color: 'text-orange-700 bg-orange-50' },
  publisher_backend_mismatch: { label: 'Publisher/backend publisher mismatch', color: 'text-purple-700 bg-purple-50' },
  missing_description: { label: 'Missing description', color: 'text-orange-700 bg-orange-50' },
  missing_genre: { label: 'Missing genre', color: 'text-orange-700 bg-orange-50' },
  no_contributors: { label: 'No contributors', color: 'text-orange-700 bg-orange-50' },
  missing_image: { label: 'Missing cover image', color: 'text-orange-700 bg-orange-50' },
  missing_audio: { label: 'Missing audio', color: 'text-orange-700 bg-orange-50' },
  partial_audio: { label: 'Some audio incomplete', color: 'text-yellow-700 bg-yellow-50' },
  missing_sample: { label: 'Missing sample clip', color: 'text-orange-700 bg-orange-50' },
  publisher_series_mismatch: { label: 'Publisher mismatch with series', color: 'text-purple-700 bg-purple-50' },
  format_mismatch: { label: 'Format issues', color: 'text-pink-700 bg-pink-50' },
  group_formats_empty: { label: 'Group formats empty', color: 'text-pink-700 bg-pink-50' },
};

export function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboard.get(),
    refetchInterval: 30_000,
  });

  // Separate, slower-polling query — dedup scan is heavier and changes far less often than catalog stats.
  const { data: dupData } = useQuery({
    queryKey: ['contributor-duplicates'],
    queryFn: () => catalogAdmin.contributorDuplicates(),
    refetchInterval: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !data) {
    return <div className="p-8 text-red-600">Failed to load dashboard</div>;
  }

  const stats = [
    { label: 'Upcoming Releases', value: data.upcomingCount, icon: CalendarDays, color: 'text-brand-600', bg: 'bg-brand-50', to: '/catalog?upcoming=true' },
    { label: 'At-Risk Items', value: data.atRiskCount, icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50', to: '/catalog?atRisk=true' },
    { label: 'Duplicate Contributors', value: dupData?.totalGroups ?? '—', icon: Users, color: 'text-red-600', bg: 'bg-red-50', to: '/contributors' },
    { label: 'Batches This Month', value: data.batchesThisMonth, icon: PackageCheck, color: 'text-green-600', bg: 'bg-green-50', to: '/batches' },
    { label: 'Mirror Items', value: data.mirrorCount ?? '—', icon: Database, color: 'text-purple-600', bg: 'bg-purple-50', to: '/catalog' },
  ];

  const lastSyncLabel = data.lastSync
    ? `Synced ${formatDistanceToNow(new Date(data.lastSync), { addSuffix: true })}`
    : 'Never synced';

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Metadata ingest pipeline overview</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <RefreshCw size={12} /> {lastSyncLabel}
          </span>
          <Link to="/upload">
            <Button><Upload size={16} /> Upload ONIX File</Button>
          </Link>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-5 gap-4 mb-8">
        {stats.map(({ label, value, icon: Icon, color, bg, to }) => (
          <Link key={label} to={to}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="flex items-center gap-4 py-5">
                <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Upcoming releases */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Upcoming Releases</span>
              <Link to="/catalog?upcoming=true" className="text-xs font-normal text-brand-600 hover:underline">View all →</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {data.upcomingItems.slice(0, 20).map((item) => (
                <Link key={item._id} to={`/catalog/${item._id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-400">{safeDate(item.releaseAt)}</p>
                  </div>
                  <Badge variant={statusBadge(safeStatus(item.status))}>{safeStatus(item.status)}</Badge>
                </Link>
              ))}
              {data.upcomingItems.length === 0 && (
                <p className="px-5 py-4 text-gray-400 text-sm">No upcoming releases in next 30 days</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* At-risk items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>At-Risk Items</span>
              <Link to="/catalog?atRisk=true" className="text-xs font-normal text-red-600 hover:underline">View all →</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {data.atRiskItems.slice(0, 20).map((item) => {
                const topFlag = item.atRiskFlags?.[0];
                const flagInfo = topFlag ? AT_RISK_LABELS[topFlag] : null;
                return (
                  <Link key={item._id} to={`/catalog/${item._id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      {flagInfo && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${flagInfo.color}`}>
                          {flagInfo.label}
                        </span>
                      )}
                    </div>
                    <Badge variant={statusBadge(safeStatus(item.status))}>{safeStatus(item.status)}</Badge>
                  </Link>
                );
              })}
              {data.atRiskItems.length === 0 && (
                <p className="px-5 py-4 text-gray-400 text-sm">No at-risk items</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Duplicate contributors */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Duplicate Contributors</span>
              <Link to="/contributors" className="text-xs font-normal text-brand-600 hover:underline">View all →</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {dupData?.groups.slice(0, 20).map((group, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                      group.confidence === 'exact' ? 'text-red-700 bg-red-50' : 'text-yellow-700 bg-yellow-50'
                    }`}
                  >
                    {group.confidence === 'exact' ? 'Exact' : 'Maybe'}
                  </span>
                  <div className="flex-1 min-w-0 flex flex-wrap gap-x-2 gap-y-1">
                    {group.members.map((m) => (
                      <Link
                        key={m._id}
                        to={`/contributors/${m._id}`}
                        className="text-sm font-medium text-gray-900 hover:text-brand-600 hover:underline truncate"
                      >
                        {m.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              {dupData && dupData.groups.length === 0 && (
                <p className="px-5 py-4 text-gray-400 text-sm">No duplicate contributors found</p>
              )}
              {!dupData && (
                <p className="px-5 py-4 text-gray-400 text-sm flex items-center gap-2">
                  <Loader2 className="animate-spin" size={14} /> Scanning for duplicates…
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent batches */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Batches</span>
              <Link to="/batches" className="text-xs font-normal text-brand-600 hover:underline">View all →</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {data.recentBatches.map((batch) => (
                <Link key={batch.id} to={`/batches/${batch.id}`} className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{batch.fileName}</p>
                    <p className="text-xs text-gray-400">
                      {batch.totalRecords} records · {formatDistanceToNow(new Date(batch.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase font-mono">
                      {batch.source === 'csv' ? 'CSV' : 'ONIX'}
                    </span>
                    <Badge variant={statusBadge(batch.status)}>{batch.status.replace(/_/g, ' ')}</Badge>
                  </div>
                </Link>
              ))}
              {data.recentBatches.length === 0 && (
                <p className="px-6 py-4 text-gray-400 text-sm">No batches yet — upload your first ONIX file</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
